<template>
  <div id="dialog-diseaseSearch">
    <div class="dialog">
      <span class="cls" @click="close"></span>
      <div class="paper">
        <div class="header">
          <h2>질병코드 조회</h2>
          <button class="close-btn" @click="close">
            <i class="ph-bold ph-x"></i>
          </button>
        </div>
        
        <div class="search-box">
          <input 
            type="text" 
            v-model="icdCode" 
            placeholder="질병코드 또는 질병명을 입력하세요" 
            @keypress.enter="findDiseaseInfo"
            ref="searchInput"
          />
          <button class="btn-search" @click="findDiseaseInfo">
            <i class="ph-bold ph-magnifying-glass"></i>
          </button>
        </div>
        
        <div class="disease-results">
          <ul v-if="diseaseList.length > 0">
            <li v-for="(disease, i) in diseaseList" :key="i" @click="selectDisease(disease)">
              <span class="code">{{ disease.sickCd }}</span>
              <span class="name">{{ disease.sickNm }}</span>
            </li>
          </ul>
          <div class="empty" v-else>
            <i class="ph ph-magnifying-glass"></i>
            <p>{{ icdCode ? '검색 결과가 없습니다' : '질병코드 또는 질병명으로 검색하세요' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, onMounted } from 'vue';

export default {
  name: 'dialog_diseaseSearch',
  emits: ['close', 'select'],
  setup(props, { emit }) {
    const getDiseaseInfo = inject('getDiseaseInfo');
    
    const icdCode = ref('');
    const diseaseList = ref([]);
    const searchInput = ref(null);

    onMounted(() => {
      searchInput.value?.focus();
    });

    function isDiseaseCode(str) {
      return /^[A-Z]\d{2}(\.\d+)?$/i.test(str);
    }

    async function findDiseaseInfo() {
      if (!icdCode.value.trim()) return;
      
      try {
        let res;
        if (isDiseaseCode(icdCode.value)) {
          res = await getDiseaseInfo(icdCode.value, '');
        } else {
          res = await getDiseaseInfo('', icdCode.value);
        }
        diseaseList.value = res || [];
      } catch (err) {
        console.error('질병 조회 오류:', err);
        diseaseList.value = [];
      }
    }

    function selectDisease(disease) {
      emit('select', { disease });
    }

    function close() {
      emit('close');
    }

    return {
      icdCode,
      diseaseList,
      searchInput,
      findDiseaseInfo,
      selectDisease,
      close,
    };
  }
}
</script>

<style scoped>
#dialog-diseaseSearch {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999999;
}

#dialog-diseaseSearch .dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(2px);
}

#dialog-diseaseSearch .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.paper {
  width: 90%;
  max-width: 560px;
  max-height: 80vh;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: 16px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h2 {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.close-btn {
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  border-radius: var(--radius-md);
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--color-surface-reverse);
}

.search-box {
  display: flex;
  gap: 10px;
}

.search-box input {
  flex: 1;
  height: 48px;
  padding: 0 16px;
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
  background: var(--color-surface-deep);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  box-sizing: border-box;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.search-box .btn-search {
  width: 48px;
  height: 48px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-xl);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.search-box .btn-search:hover {
  opacity: 0.9;
}

.disease-results {
  flex: 1;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  background: var(--color-surface-deep);
  border-radius: var(--radius-md);
}

.disease-results::-webkit-scrollbar {
  width: 4px;
}

.disease-results::-webkit-scrollbar-thumb {
  background: var(--color-text-secondary);
  border-radius: 2px;
}

.disease-results ul {
  list-style: none;
  padding: 8px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.disease-results li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--color-background);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
}

.disease-results li:hover {
  background: var(--color-surface-light);
  transform: translateX(4px);
}

.disease-results li .code {
  min-width: 70px;
  padding: 4px 10px;
  background: var(--color-brand);
  color: var(--color-text-black);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-sm);
  text-align: center;
}

.disease-results li .name {
  flex: 1;
  font-size: var(--font-size-md);
  color: var(--color-text-primary);
}

.disease-results .empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
  color: var(--color-text-secondary);
}

.disease-results .empty i {
  font-size: 48px;
  opacity: 0.4;
}

.disease-results .empty p {
  margin: 0;
  font-size: var(--font-size-md);
}
</style>